import React, { useRef } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { useAnalyticsContext } from 'app/shared/utils';
import { useIsTShirtSizeBreakpointSize as useIsBreakpointSize } from 'app/shared/utils/useBreakpoint';
import { Icon, Typography } from 'app/shared/components/atoms';
import { ActionLink } from 'app/shared/components/atoms';
import { Expandable } from 'app/fan/components/atoms';
import { ReactComponent as ChevronDown } from 'icons/chevron-down.svg';

interface ArtistDescriptionProps {
  artistId?: number;
  text?: string;
}

const ArtistDescription: React.FC<
  React.PropsWithChildren<ArtistDescriptionProps>
> = ({ text, artistId }) => {
  const { isMobile, isDesktop } = useIsBreakpointSize();
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const { colors } = useCurrentTheme();
  const { formatMessage } = useIntl();

  const defaultHeight = isMobile ? 130 : 105;
  const typographyRef = useRef<HTMLParagraphElement>(null);
  const clientHeight = typographyRef.current
    ? typographyRef.current.clientHeight
    : defaultHeight + 1;

  const handleClick = () => {
    if (isExpanded && !isDesktop) {
      typographyRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsExpanded((prev) => !prev);
    trackAnalyticsEvent('Artist Description Read More/Less', {
      readMore: !isExpanded,
      artistId,
    });
  };

  return (
    <>
      <Expandable
        collapsedHeight={`${defaultHeight}px`}
        isExpanded={isExpanded}
        transitionSpeed={500}
      >
        <Typography ref={typographyRef} color={colors.primary.white.base}>
          {text}
        </Typography>
      </Expandable>
      {clientHeight > defaultHeight && (
        <ActionLink
          variant="primary"
          onClick={handleClick}
          textDecoration="none"
          textTransform="uppercase"
          flexDirection="row"
          alignItems="center"
          gap={2}
          svgFillColor="none"
          fontSize="fontSize100"
          pt={4}
        >
          <Icon
            icon={ChevronDown}
            transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
            transition="transform 0.1s"
            size="xs"
          />
          {formatMessage({
            id: isExpanded
              ? 'event.artistDescription.readLess'
              : 'event.artistDescription.readMore',
          })}
        </ActionLink>
      )}
    </>
  );
};

export default ArtistDescription;
